/**
 * Accounts - every SNF and ALF as a sales account, filtered to what a rep
 * works: their own book, a region, a stage.
 *
 * The territory managers on the TM board have mostly left, so ownership is
 * eMerge's: "Assign a region" gives every unassigned facility in a region to
 * one rep in a single step, and every change is recorded.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Search, Users } from 'lucide-react';

import { Button } from '../../../components/shadcn/ui/button';
import { Input } from '../../../components/shadcn/ui/input';
import { Account, Meta, bdApi, fmtDate, STAGE_LABEL, words } from './bdApi';
import { NativeSelect, StagePill, StateLine } from './bdUi';

const PAGE = 50;

export default function AccountsView({ meta, onOpen, refreshKey }: { meta: Meta; onOpen: (id: string) => void; refreshKey: number }) {
  const [owner, setOwner] = useState('mine');
  const [region, setRegion] = useState('');
  const [stage, setStage] = useState('');
  const [type, setType] = useState('SNF');
  const [sort, setSort] = useState('name');
  const [q, setQ] = useState('');
  const [query, setQuery] = useState('');
  const [offset, setOffset] = useState(0);
  const [data, setData] = useState<{ total: number; rows: Account[] } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    const r = await bdApi.accounts({ owner, region, stage, type, q: query, sort, limit: PAGE, offset });
    setLoading(false);
    if (r.success) setData(r.data); else setError(r.error || 'Accounts could not be loaded.');
  }, [owner, region, stage, type, query, sort, offset]);
  useEffect(() => { load(); }, [load, refreshKey]);
  useEffect(() => { setOffset(0); }, [owner, region, stage, type, query, sort]);

  return (
    <div className="space-y-4">
      <form
        className="flex flex-wrap items-end gap-3"
        onSubmit={(e) => { e.preventDefault(); setQuery(q.trim()); }}
        role="search"
      >
        <NativeSelect label="Owner" value={owner} onChange={setOwner}>
          <option value="mine">Mine</option>
          <option value="all">Everyone</option>
          <option value="unassigned">Unassigned</option>
          {meta.reps.filter((r) => r.id !== meta.me.id).map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
        </NativeSelect>
        <NativeSelect label="Region" value={region} onChange={setRegion}>
          <option value="">All regions</option>
          {meta.regions.map((r) => <option key={r.region} value={r.region}>{r.region}</option>)}
        </NativeSelect>
        <NativeSelect label="Stage" value={stage} onChange={setStage}>
          <option value="">Any stage</option>
          <option value={meta.stages.filter((s) => s.open).map((s) => s.key).join(',')}>Open pipeline</option>
          {meta.stages.map((s) => <option key={s.key} value={s.key}>{STAGE_LABEL[s.key]}</option>)}
        </NativeSelect>
        <NativeSelect label="Type" value={type} onChange={setType}>
          <option value="SNF">SNF</option>
          <option value="ALF">ALF</option>
          <option value="ALL">SNF + ALF</option>
        </NativeSelect>
        <NativeSelect label="Sort" value={sort} onChange={setSort}>
          <option value="name">Name</option>
          <option value="next_step">Next step date</option>
          <option value="recent">Last touched</option>
        </NativeSelect>
        <div className="flex items-end gap-2">
          <Input aria-label="Search facilities" placeholder="Name, city or corporation" value={q} onChange={(e) => setQ(e.target.value)} className="w-56" />
          <Button type="submit" size="sm" variant="outline"><Search className="w-4 h-4" /><span className="sr-only">Search</span></Button>
        </div>
      </form>

      <AssignRegion meta={meta} onDone={load} />

      <StateLine loading={loading && !data} error={error} onRetry={load}
        empty={data && data.rows.length === 0 ? (owner === 'mine' ? 'No accounts are assigned to you yet - try Everyone or Unassigned, or assign a region below the filters.' : 'No accounts match these filters.') : null} />

      {data && data.rows.length > 0 && (
        <>
          <div className="overflow-x-auto rounded-lg border">
            <table className="w-full text-sm">
              <thead className="bg-muted/40">
                <tr className="text-left text-muted-foreground">
                  <th className="py-2 px-3 font-medium">Facility</th>
                  <th className="py-2 px-3 font-medium">Region</th>
                  <th className="py-2 px-3 font-medium">Stage</th>
                  <th className="py-2 px-3 font-medium">Owner</th>
                  <th className="py-2 px-3 font-medium text-right">Beds</th>
                  <th className="py-2 px-3 font-medium">Last touch</th>
                  <th className="py-2 px-3 font-medium">Next step</th>
                </tr>
              </thead>
              <tbody>
                {data.rows.map((a) => (
                  <tr key={a.facility_id} className="border-t hover:bg-muted/30">
                    <td className="py-2 px-3">
                      <button type="button" className="text-left font-medium hover:underline" onClick={() => onOpen(a.facility_id)}>{a.name}</button>
                      <div className="text-xs text-muted-foreground">{[a.city, a.facility_type, a.current_provider && `vs ${a.current_provider}`].filter(Boolean).join(' · ')}</div>
                    </td>
                    <td className="py-2 px-3 text-muted-foreground">{a.region || '—'}</td>
                    <td className="py-2 px-3"><StagePill stage={a.stage} /></td>
                    <td className="py-2 px-3">{a.owner_name || <span className="text-muted-foreground">—</span>}</td>
                    <td className="py-2 px-3 text-right" style={{ fontVariantNumeric: 'tabular-nums' }}>{a.beds ?? '—'}</td>
                    <td className="py-2 px-3 text-muted-foreground">
                      {a.last_activity_at ? `${fmtDate(a.last_activity_at)}${a.last_disposition ? ` · ${words(a.last_disposition)}` : ''}` : '—'}
                    </td>
                    <td className="py-2 px-3">{a.next_step ? `${a.next_step}${a.next_step_on ? ` · ${fmtDate(a.next_step_on)}` : ''}` : <span className="text-muted-foreground">—</span>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="flex items-center gap-3 text-sm">
            <span className="text-muted-foreground" style={{ fontVariantNumeric: 'tabular-nums' }}>
              {offset + 1}–{offset + data.rows.length} of {data.total}
            </span>
            <Button size="sm" variant="outline" disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - PAGE))}>Previous</Button>
            <Button size="sm" variant="outline" disabled={offset + PAGE >= data.total} onClick={() => setOffset(offset + PAGE)}>Next</Button>
          </div>
        </>
      )}
    </div>
  );
}

function AssignRegion({ meta, onDone }: { meta: Meta; onDone: () => void }) {
  const [open, setOpen] = useState(false);
  const [rep, setRep] = useState('');
  const [region, setRegion] = useState('');
  const [type, setType] = useState<'SNF' | 'ALF' | 'ALL'>('SNF');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);

  if (!open) {
    return (
      <button type="button" onClick={() => setOpen(true)} className="text-sm underline underline-offset-2 flex items-center gap-1">
        <Users className="w-4 h-4" /> Assign a region to a rep
      </button>
    );
  }
  const repName = meta.reps.find((r) => r.id === rep)?.name;
  return (
    <form
      className="rounded-lg border p-3 flex flex-wrap items-end gap-3"
      onSubmit={async (e) => {
        e.preventDefault();
        if (!rep || !region) return;
        setBusy(true); setMsg(null);
        const r = await bdApi.assign({ owner_user_id: rep, region, type, only_unassigned: true });
        setBusy(false);
        if (!r.success) { setMsg({ ok: false, text: r.error || 'Not assigned.' }); return; }
        setMsg({ ok: true, text: `${r.data.changed} unassigned ${type === 'ALL' ? 'facilities' : `${type}s`} in ${region} now belong to ${repName}.` });
        onDone();
      }}
    >
      <NativeSelect label="Rep" value={rep} onChange={setRep}>
        <option value="">Choose…</option>
        {meta.reps.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
      </NativeSelect>
      <NativeSelect label="Region" value={region} onChange={setRegion}>
        <option value="">Choose…</option>
        {meta.regions.map((r) => <option key={r.region} value={r.region}>{r.region} ({r.facilities})</option>)}
      </NativeSelect>
      <NativeSelect label="Type" value={type} onChange={(v) => setType(v as 'SNF' | 'ALF' | 'ALL')}>
        <option value="SNF">SNFs</option>
        <option value="ALF">ALFs</option>
        <option value="ALL">Both</option>
      </NativeSelect>
      <Button type="submit" size="sm" disabled={busy || !rep || !region}>{busy ? 'Assigning…' : 'Assign unassigned'}</Button>
      <Button type="button" size="sm" variant="ghost" onClick={() => { setOpen(false); setMsg(null); }}>Close</Button>
      <p className="basis-full text-xs text-muted-foreground">Only facilities with no owner are given; a facility someone already owns is left with them.</p>
      {msg && <p role={msg.ok ? 'status' : 'alert'} className={`basis-full text-sm ${msg.ok ? 'text-emerald-700' : 'text-destructive'}`}>{msg.text}</p>}
    </form>
  );
}
